import { useMemo, useState } from "react";
import { Building2, Check, ChevronDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

/**
 * Phase 5C.1b — Workspace switcher.
 *
 * Rules enforced here (match the founder's acceptance list A-N):
 *  - renders only `accepted` memberships returned by
 *    GET /api/auth/memberships (M = any revoked / suspended rows are
 *    never shown; the server already filters by status=accepted);
 *  - primary label is human-readable (first+last name /
 *    workspace_display_name) — never UUID;
 *  - UUID is NOT exposed to the end user, only used as the
 *    internal `workspace_owner` id when posting /switch-workspace;
 *  - hidden entirely for users who only have their own workspace
 *    (M = owner-only legacy UX unchanged);
 *  - switching immediately hard-reloads /dashboard so no
 *    stale WS-A data remains visible in WS-B (D);
 *  - clicking the currently-active workspace is a no-op.
 */
export default function WorkspaceSwitcher() {
  const { memberships, activeWorkspace, switchWorkspace, user,
    membershipsError, membershipsLoading, fetchMemberships } = useAuth();
  const [busy, setBusy] = useState(false);

  const activeOwner = activeWorkspace || user?.id;
  const current = useMemo(
    () => (memberships || []).find((m) => m.workspace_owner === activeOwner),
    [memberships, activeOwner]
  );
  const missingCurrent = !!activeOwner && !current;
  const ownerOnly = (memberships || []).length === 1 &&
    current?.is_self && current.workspace_owner === user?.id;

  // M acceptance rule: owner-only accounts see nothing — the switcher
  // must not appear at all for legacy single-workspace users.
  // Hook calls above must stay unconditional (React rules of hooks).
  if (!memberships?.length || ownerOnly) {
    if (!membershipsError) return null;
    return (
      <button type="button" disabled={membershipsLoading} onClick={() => fetchMemberships()}
        title={membershipsError} className="rounded border px-3 py-1.5 text-xs disabled:opacity-50"
        data-testid="workspace-list-retry">
        {membershipsLoading ? "Loading workspaces…" : "Retry workspace list"}
      </button>
    );
  }

  const currentLabel =
    current?.workspace_display_name ||
    (current?.is_self
      ? `${user?.first_name || ""} ${user?.last_name || ""}`.trim() ||
        user?.email ||
        "My workspace"
      : missingCurrent ? "Active workspace" : "Workspace");

  async function handleSwitch(target) {
    if (busy || membershipsLoading || membershipsError) return;
    if (target.workspace_owner === activeOwner) return;
    setBusy(true);
    try {
      await switchWorkspace(target.workspace_owner);
      // No toast — the hard reload immediately follows; a toast would
      // flash and vanish.
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg =
        (detail && typeof detail === "object" && detail.message) ||
        (typeof detail === "string" && detail) ||
        "Could not switch workspace.";
      toast.error(msg);
      setBusy(false);
    }
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="workspace-switcher"
          className="flex items-center gap-2 px-3 py-1.5 rounded-md border border-[#E5E8F0] bg-white hover:bg-[#F4F6FB] transition-colors"
          disabled={busy || membershipsLoading}
        >
          <Building2 size={14} className="text-[#202B60]" />
          <span
            className="text-[12.5px] font-semibold text-black max-w-[180px] truncate"
            data-testid="workspace-switcher-current-label"
            title={currentLabel}
          >
            {currentLabel}
          </span>
          <ChevronDown size={12} className="text-[#666]" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-72"
        data-testid="workspace-switcher-menu"
      >
        <DropdownMenuLabel>
          <div className="text-[11px] uppercase tracking-wide text-[#666] font-semibold">
            Switch workspace
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {(membershipsError || missingCurrent) && <>
          <DropdownMenuLabel><p role="alert" className="whitespace-normal text-xs text-amber-700">{membershipsError || "The active workspace is not in this list. Refresh the list before choosing another workspace."}</p></DropdownMenuLabel>
          <DropdownMenuItem disabled={membershipsLoading}
            onSelect={(event) => { event.preventDefault(); fetchMemberships(); }}>
            {membershipsLoading ? "Loading workspaces…" : "Retry workspace list"}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
        </>}
        {memberships.map((m) => {
          const isCurrent = m.workspace_owner === activeOwner;
          const label =
            m.workspace_display_name ||
            (m.is_self
              ? `${user?.first_name || ""} ${user?.last_name || ""}`.trim() ||
                user?.email ||
                "My workspace"
              : "Workspace");
          return (
            <DropdownMenuItem
              key={m.workspace_owner}
              disabled={busy || membershipsLoading || !!membershipsError}
              data-testid={`workspace-option-${m.workspace_owner}`}
              onClick={() => handleSwitch(m)}
              className="flex items-center justify-between py-2"
            >
              <div className="min-w-0">
                <div className="text-[13px] font-medium text-black truncate">
                  {label}
                </div>
                <div className="text-[11px] text-[#666]">
                  {m.is_self ? "Owner" : `${m.role || "Member"} · L${m.level ?? "-"}`}
                </div>
              </div>
              {isCurrent && (
                <Check
                  size={14}
                  className="text-[#202B60] ml-2 shrink-0"
                  data-testid={`workspace-option-${m.workspace_owner}-check`}
                />
              )}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
